import type { McpConnectedClient } from '@simplemon/lib';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { McpSignInRequiredError, mcpClient } from '@/lib/mcp-client';

function displayTime(value: string | null): string {
  if (!value) return 'Not used yet';
  return new Date(value).toLocaleString();
}

export function ConnectedClientsPage() {
  const [clients, setClients] = useState<McpConnectedClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signInRequired, setSignInRequired] = useState(false);
  const [disconnecting, setDisconnecting] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSignInRequired(false);
    try {
      setClients(await mcpClient.list());
    } catch (cause) {
      if (cause instanceof McpSignInRequiredError) setSignInRequired(true);
      setError(cause instanceof Error ? cause.message : 'Could not load connected clients.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  async function disconnect(client: McpConnectedClient) {
    setDisconnecting(client.grantId);
    setError(null);
    setSignInRequired(false);
    setNotice(null);
    try {
      await mcpClient.disconnect(client.grantId);
      setClients((current) => current.filter((entry) => entry.grantId !== client.grantId));
      setNotice(`Disconnected ${client.clientName} from ${client.organizationName}.`);
    } catch (cause) {
      if (cause instanceof McpSignInRequiredError) setSignInRequired(true);
      setError(cause instanceof Error ? cause.message : 'Could not disconnect client. Try again.');
    } finally {
      setDisconnecting(null);
    }
  }

  return (
    <div className="px-4 py-8 sm:px-6 lg:px-8">
      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b px-6 py-4">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Connected clients</h1>
            <p className="mt-1 max-w-prose text-sm text-gray-600">
              Manage clients you allowed to access Valvo. Disconnecting stops their access and keeps
              your monitors.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => void load()}
            disabled={loading || disconnecting !== null}
          >
            Refresh
          </Button>
        </div>
        {notice && (
          <output className="block px-6 py-4 text-sm text-green-800 break-words">{notice}</output>
        )}
        {error && (
          <div role="alert" className="px-6 py-4">
            <p className="text-sm text-red-700">{error}</p>
            {signInRequired && (
              <Link
                to="/login"
                className="mt-3 inline-block text-sm font-medium text-blue-700 underline focus-visible:outline-2 focus-visible:outline-blue-600"
              >
                Sign in again
              </Link>
            )}
            <Button
              className="mt-3"
              variant="outline"
              onClick={() => void load()}
              disabled={loading}
            >
              Retry
            </Button>
          </div>
        )}
        {loading ? (
          <output className="block px-6 py-12 text-center text-sm text-gray-600">
            Loading connected clients…
          </output>
        ) : !error && clients.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <h2 className="font-medium text-gray-900">No connected clients</h2>
            <p className="mt-2 text-sm text-gray-600">
              Start a connection from your MCP client and approve access in Valvo.
            </p>
          </div>
        ) : (
          <ul className="divide-y">
            {clients.map((client) => (
              <li
                key={client.grantId}
                className="flex flex-col gap-4 px-6 py-5 sm:flex-row sm:items-start sm:justify-between"
              >
                <div className="min-w-0 space-y-2">
                  <h2 className="break-words font-semibold text-gray-900">{client.clientName}</h2>
                  <p className="text-xs text-gray-500">Name supplied by the client</p>
                  <p className="break-words text-sm text-gray-700">
                    {client.organizationName} ·{' '}
                    {client.scopes.includes('monitors:write') ? 'Read and write' : 'Read only'}
                  </p>
                  <dl className="space-y-1 text-sm text-gray-600">
                    <div>
                      <dt className="inline">Connected: </dt>
                      <dd className="inline">{displayTime(client.createdAt)}</dd>
                    </div>
                    <div>
                      <dt className="inline">Last used: </dt>
                      <dd className="inline">{displayTime(client.lastUsedAt)}</dd>
                    </div>
                  </dl>
                </div>
                <Button
                  variant="outline"
                  className="shrink-0"
                  aria-label={`Disconnect ${client.clientName} from ${client.organizationName}`}
                  disabled={disconnecting !== null}
                  onClick={() => void disconnect(client)}
                >
                  {disconnecting === client.grantId ? 'Disconnecting…' : 'Disconnect'}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
